import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { auth, type MeResponse } from '../api/auth'
import { ApiError } from '../api/client'

/**
 * AuthContext is the single source of truth for "who is logged in" across
 * the web-app. The cookie session lives in the browser; we mirror its state
 * here for routing guards + UI affordances.
 *
 * Boot flow: AuthProvider mounts → `me()` against the cookie. On 401 we land
 * as `anonymous`. After login/signup the caller invokes `refresh()` to pick
 * up the new identity. Logout clears both the cookie (server-side) and our
 * mirror.
 */

type Status = 'idle' | 'hydrating' | 'authenticated' | 'anonymous'

interface AuthState {
  status: Status
  user: MeResponse | null
  error: string | null
  /** Pull the cookie-session identity from the backend. Returns the user (or null). */
  refresh: () => Promise<MeResponse | null>
  login: (identifier: string, password: string) => Promise<MeResponse>
  signup: (email: string, password: string, displayName?: string) => Promise<MeResponse>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('idle')
  const [user, setUser] = useState<MeResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setStatus('hydrating')
    try {
      const me = await auth.me()
      setUser(me)
      setError(null)
      setStatus('authenticated')
      return me
    } catch (e) {
      setUser(null)
      if (!(e instanceof ApiError) || e.status !== 401) {
        setError(e instanceof Error ? e.message : 'Error')
      }
      setStatus('anonymous')
      return null
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const root = document.documentElement.style
    if (user?.branding_club) {
      const { primary_color, secondary_color, accent_color } = user.branding_club
      if (primary_color) root.setProperty('--club-primary', primary_color)
      if (secondary_color) root.setProperty('--club-secondary', secondary_color)
      if (accent_color) root.setProperty('--club-accent', accent_color)
    } else {
      root.removeProperty('--club-primary')
      root.removeProperty('--club-secondary')
      root.removeProperty('--club-accent')
    }
  }, [user?.branding_club])

  const login = useCallback<AuthState['login']>(
    async (identifier, password) => {
      setError(null)
      await auth.login(identifier, password)
      const me = await refresh()
      if (!me) throw new Error('Login OK but session not hydrated')
      return me
    },
    [refresh],
  )

  const signup = useCallback<AuthState['signup']>(
    async (email, password, displayName) => {
      setError(null)
      await auth.signup(email, password, displayName)
      const me = await refresh()
      if (!me) throw new Error('Signup OK but session not hydrated')
      return me
    },
    [refresh],
  )

  const logout = useCallback<AuthState['logout']>(async () => {
    await auth.logout()
    setUser(null)
    setStatus('anonymous')
  }, [])

  const value = useMemo<AuthState>(
    () => ({ status, user, error, refresh, login, signup, logout }),
    [status, user, error, refresh, login, signup, logout],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
